import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PlusCircle, FileText, Layers, Trash2 } from "lucide-react";
import { examsApi } from "@/services/api";

export default function ExamsPage() {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    order: "",
    group: "",
    duration: 10,
  });

  useEffect(() => {
    loadExams();
  }, []);

  const loadExams = async () => {
    try {
      setLoading(true);
      const res = await examsApi.getAll();
      setExams(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      console.error("Exams loading error:", error);
      setExams([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      order: "",
      group: "",
      duration: 10,
    });
  };

  const handleCreateExam = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.group) {
      alert("Sınaq adı və qrup seçilməlidir");
      return;
    }

    try {
      setSubmitting(true);

      await examsApi.create({
        name: formData.name.trim(),
        order: formData.order !== "" ? Number(formData.order) : 0,
        group: formData.group,
        duration: formData.duration !== "" ? Number(formData.duration) : 10,
      });

      resetForm();
      await loadExams();
    } catch (error) {
      console.error("Create exam error:", error);
      alert(error?.response?.data?.message || "Exam yaradılarkən xəta baş verdi");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteExam = async (id) => {
    const confirmDelete = window.confirm("Bu exam silinsin?");
    if (!confirmDelete) return;

    try {
      await examsApi.delete(id);
      await loadExams();
    } catch (error) {
      console.error("Delete exam error:", error);
      alert(error?.response?.data?.message || "Exam silinərkən xəta baş verdi");
    }
  };

  
  
  return (
    <Layout breadcrumbs={[{ label: "Examlar" }]}>
      <div className="space-y-8" data-testid="exams-page">
        <div>
          <h1
            className="text-2xl font-bold text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Exam Yönetimi
          </h1>
          <p className="mt-1 text-slate-500">
            Əvvəl exam yarat, sonra sualları əlavə et
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <PlusCircle className="h-5 w-5 text-teal-600" />
              Yeni Exam Ekle
            </CardTitle>
          </CardHeader>

          <CardContent>
            <form
              onSubmit={handleCreateExam}
              className="grid grid-cols-1 gap-4 md:grid-cols-4"
            >
              <input
                type="text"
                name="name"
                placeholder="Sınaq adı"
                value={formData.name}
                onChange={handleChange}
                className="h-11 rounded-lg border border-slate-200 px-3 outline-none focus:border-teal-500"
                required
              />

              <select
                name="group"
                value={formData.group}
                onChange={handleChange}
                className="h-11 rounded-lg border border-slate-200 px-3 outline-none focus:border-teal-500"
                required
              >
                <option value="">Qrup seç</option>
                <option value="1">1-ci qrup</option>
                <option value="2">2-ci qrup</option>
                <option value="3">3-cü qrup</option>
                <option value="4">4-cü qrup</option>
                <option value="5">5-ci qrup</option>
              </select>

              <input
                type="number"
                name="order"
                placeholder="Sıra"
                value={formData.order}
                onChange={handleChange}
                className="h-11 rounded-lg border border-slate-200 px-3 outline-none focus:border-teal-500"
              />

              <button
                type="submit"
                disabled={submitting}
                className="h-11 rounded-lg bg-teal-600 font-medium text-white transition-colors hover:bg-teal-700 disabled:opacity-50"
              >
                {submitting ? "Ekleniyor..." : "Exam Ekle"}
              </button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Bütün Examlar</CardTitle>
          </CardHeader>

          <CardContent>
            {loading ? (
              <p className="text-slate-500">Yükleniyor...</p>
            ) : exams.length === 0 ? (
              <div className="py-10 text-center">
                <FileText className="mx-auto h-12 w-12 text-slate-300" />
                <p className="mt-3 text-slate-500">Henüz exam yoxdur</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {exams.map((exam) => (
                  <Card
                    key={exam._id}
                    className="border transition-colors hover:border-teal-200"
                    data-testid={`exam-card-${exam._id}`}
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between gap-3">
                        <Link to={`/exams/${exam._id}/questions`} className="flex-1">
                          <div className="space-y-2">
                            <div className="flex items-center gap-3">
                              <div className="rounded-xl bg-teal-50 p-3 text-teal-600">
                                <Layers className="h-5 w-5" />
                              </div>

                              <div>
                                <h3 className="font-semibold text-slate-900">{exam.name}</h3>
                                <p className="text-sm text-slate-500">Qrup: {exam.group}</p>
                                <p className="text-sm text-slate-500">{exam._id}</p>
                              </div>
                            </div>

                            <div className="pt-2 text-sm text-slate-500">
                              <p>Sıra: {exam.order ?? "-"}</p>
                              <p>Sual sayı: {exam.questionCount ?? 0}</p>
                              <p>Hər sualın balı: {exam.questionPoint ?? 0}</p>
                              <p>Müddət: {exam.duration ?? 10} dəq</p>
                            </div>
                          </div>
                        </Link>

                        <button
                          type="button"
                          onClick={() => handleDeleteExam(exam._id)}
                          className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}